import {
  AddressQueueSummary,
  AnalyticsReport,
  AddressSuggestionResult,
  AwaitingItem,
  Corridor,
  Delivery,
  Driver,
  DriverLink,
  ExceptionDecisionOption,
  FillEstimate,
  GeocodeRun,
  GoodsInVehicle,
  NetworkLocation,
  ObstacleGroup,
  PendingLocation,
  OpenException,
  PackingList,
  PendingAddress,
  PostingToDo,
  Reconciliation,
  RoadRoute,
  RouteTemplate,
  SavedTour,
  SignedInUser,
  Settings,
  TourPreview,
  TourDetail,
  TourProgress,
  UnresolvedDocument,
  TourProposal,
  Vehicle
} from '../types';

import { API_BASE as BASE } from './apiBase';

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return response.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return response.json() as Promise<T>;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return response.json() as Promise<T>;
}

async function del<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { method: 'DELETE', credentials: 'include' });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return response.json() as Promise<T>;
}

/** Poruka mora reći šta je palo, da operater ne gleda prazan ekran bez objašnjenja. */
async function describeFailure(response: Response, path: string): Promise<string> {
  const detail = await response.text().catch(() => '');
  if (response.status === 401) {
    return 'Prijava je istekla. Osveži stranicu i prijavi se ponovo.';
  }
  if (response.status === 403) {
    return 'Ovaj objekat nije tvoj — poslovođa vidi samo svoju prodaju.';
  }
  if (response.status === 503) {
    return 'ERP baza trenutno nije dostupna. Podaci koji se vide mogu biti zastareli.';
  }
  return `Zahtev ${path} nije uspeo (${response.status}). ${detail}`.trim();
}

export const api = {
  awaiting: (maxWaitDays: number) =>
    get<AwaitingItem[]>(`/api/transport/awaiting?maxWaitDays=${maxWaitDays}`),

  reconciliation: () => get<Reconciliation>('/api/transport/awaiting/reconciliation'),

  deliveries: (maxWaitDays: number) =>
    get<Delivery[]>(`/api/transport/deliveries?maxWaitDays=${maxWaitDays}`),

  vehicles: () => get<Vehicle[]>('/api/planning/vehicles'),

  drivers: (date?: string) =>
    get<Driver[]>(`/api/planning/drivers${date ? `?date=${date}` : ''}`),

  settings: () => get<Settings>('/api/planning/settings'),

  /**
   * Pravi vozaču nov link. Stari time prestaje da važi — što je i smisao kad se
   * telefon izgubi ili vozač ode.
   */
  createDriverLink: (id: number) =>
    post<DriverLink>(`/api/planning/drivers/${id}/link`, {}),

  /** Poslovođa javlja da je roba naručena i kad stiže. Ne dira ERP. */
  promiseGoods: (code: string, body: {
    documentKey: string;
    articleCode: string;
    expectedKind: string;
    expectedCode: string;
    expectedOn: string;
    note: string | null;
  }) => post<{ upisano: boolean }>(`/api/store/${code}/expected`, body),

  locations: () => get<NetworkLocation[]>('/api/locations'),

  /** Objekti kojima koordinata nije potvrđena — bez toga ne mogu u turu. */
  pendingLocations: () => get<PendingLocation[]>('/api/locations/pending-verification'),

  confirmLocation: (id: number, latitude: number, longitude: number) =>
    post<{ id: number; isVerified: boolean }>(`/api/locations/${id}/confirm`, { latitude, longitude }),

  /** Šta ne može u turu i zašto, skupljeno na jedno mesto. */
  obstacles: () => get<ObstacleGroup[]>('/api/transport/obstacles'),

  /** Nalozi kojima ERP nije rekao gde idu. */
  unresolvedDocuments: () =>
    get<UnresolvedDocument[]>('/api/transport/awaiting/unresolved'),

  /** Operaterov upis odredišta. Ide samo u našu bazu, ne u ERP. */
  recordDestination: (body: {
    sourceDocument: string;
    destinationKind: string;
    destinationCode: string;
    rawText: string | null;
    note: string | null;
    learnPhrase: boolean;
  }) => post<{ upisano: boolean }>('/api/transport/awaiting/destination', body),

  fillEstimate: (vehicleId: number, items: { articleCode: string; quantity: number }[]) =>
    post<FillEstimate>('/api/planning/fill-estimate', { vehicleId, items }),

  templates: (date?: string) =>
    get<RouteTemplate[]>(`/api/planning/templates${date ? `?date=${date}` : ''}`),

  proposal: (templateId: number, date?: string) =>
    get<TourProposal>(`/api/planning/templates/${templateId}/proposal${date ? `?date=${date}` : ''}`),

  /**
   * Stvarni put kroz zadate tačke. Traži se kad operater izmeni predlog —
   * linija i kilometraža iz predloga tad govore o turi koja više ne postoji.
   */
  road: (points: { latitude: number; longitude: number; label?: string }[]) =>
    post<RoadRoute>('/api/planning/road', { points }),

  previewTour: (draft: unknown) => post<TourPreview>('/api/planning/tour/preview', draft),

  /** Predlog boljeg redosleda za već složenu turu. Ne dodaje i ne izbacuje stanice. */
  suggestOrder: (draft: unknown) =>
    post<{
      changed: boolean;
      order?: number[];
      beforeKm?: number;
      afterKm?: number;
      savedKm?: number;
      measured?: boolean;
    }>('/api/planning/tour/order', draft),

  saveTour: (draft: unknown) => post<SavedTour>('/api/planning/tour', draft),

  /**
   * Predlozi ispravke naziva ulice. Zove se tek kad operater otvori adresu —
   * savetnik se plaća po pozivu, a predlog vredi samo dok neko gleda u ekran.
   */
  suggestAddress: (id: number) =>
    post<AddressSuggestionResult>(`/api/addresses/${id}/suggest`, {}),

  addressQueue: (limit = 100) => get<PendingAddress[]>(`/api/addresses/queue?limit=${limit}`),

  addressSummary: () => get<AddressQueueSummary>('/api/addresses/queue/summary'),

  runGeocoding: (max = 50, retry = false) =>
    post<GeocodeRun>(`/api/addresses/geocode?max=${max}&retry=${retry}`, {}),

  confirmAddress: (id: number, body: unknown) =>
    post<{ id: number }>(`/api/addresses/${id}/confirm`, body),

  rejectAddress: (id: number, reason: string, rejectedBy: string) =>
    post<{ id: number }>(`/api/addresses/${id}/reject`, { reason, rejectedBy }),

  tourProgress: (date?: string) =>
    get<TourProgress[]>(`/api/monitor/tours${date ? `?date=${date}` : ''}`),

  /** Stanice jedne ture. Traži se tek kad operater otvori turu. */
  tourDetail: (tourId: number) => get<TourDetail>(`/api/monitor/tours/${tourId}`),

  /** Odbacuje turu koja se neće voziti — roba se time oslobađa. */
  abandonTour: (tourId: number, reason: string) =>
    post<{ odbaceno: boolean }>(`/api/monitor/tours/${tourId}/abandon`, { reason }),

  /** Nalog za pakovanje: šta se tovari i kojim redom. */
  packingList: (tourId: number) => get<PackingList>(`/api/monitor/tours/${tourId}/packing`),

  /** Planirano protiv ostvarenog za prozor od N dana. */
  analytics: (days: number) => get<AnalyticsReport>(`/api/monitor/analytics?days=${days}`),

  /** Moguće odluke o robi koja je prenoćila u vozilu. */
  leftoverDecisions: () =>
    get<ExceptionDecisionOption[]>('/api/monitor/leftover/decisions'),

  /** Šta je urađeno sa robom iz vozila. Ne dira ERP — beleži se šta je fizički urađeno. */
  decideOnLeftover: (tourId: number, decision: string, note: string | null) =>
    post<{ zabelezeno: boolean }>(`/api/monitor/tours/${tourId}/leftover`, { decision, note }),

  openExceptions: () => get<OpenException[]>('/api/monitor/exceptions'),

  exceptionDecisions: () => get<ExceptionDecisionOption[]>('/api/monitor/exceptions/decisions'),

  resolveException: (stopId: number, decision: string, note: string | null, resolvedBy: string) =>
    post<{ stopId: number }>(`/api/monitor/exceptions/${stopId}/resolve`, { decision, note, resolvedBy }),

  goodsInVehicles: () => get<GoodsInVehicle[]>('/api/monitor/goods-in-vehicles'),

  /** Nalozi u ERP-u koje čovek treba da proknjiži. Aplikacija knjiženje ne dira. */
  postings: () => get<PostingToDo[]>('/api/erp/postings'),

  /**
   * Beleži da je nalog u ERP-u poništen rukom. Aplikacija to ne zaključuje sama —
   * prolazna greška u čitanju ne sme da izgleda kao brisanje.
   */
  withdrawWrite: (idempotencyKey: string, confirmedBy: string, note: string | null) =>
    post<{ documentNumber: string; message: string }>(
      '/api/erp/writes/withdraw', { idempotencyKey, confirmedBy, note }),

  /** Ko je prijavljen. Frontend ovim počinje — bez odgovora ne prikazuje ekran. */
  me: () => get<SignedInUser>('/api/auth/me'),

  login: (username: string, password: string) =>
    post<SignedInUser>('/api/auth/login', { username, password }),

  logout: () => post<{ odjavljen: boolean }>('/api/auth/logout', {}),

  /** Pravci sa mestima kroz koja prolaze. Koordinate su predlog dok se ne potvrde. */
  corridors: () => get<Corridor[]>('/api/corridors'),

  /** Naziv, nedeljni raspored i upotreba pravca. */
  updateCorridor: (id: number, name: string, dayFlags: number, isActive: boolean) =>
    put<{ id: number }>(`/api/corridors/${id}`, { name, dayFlags, isActive }),

  /** Ceo spisak objekata pravca, redom. Šalje se u celini, jer je redosled svojstvo spiska. */
  setCorridorStops: (id: number, stops: { kind: string; code: string }[]) =>
    put<{ id: number }>(`/api/corridors/${id}/stops`, { stops }),

  /** Dodaje mesto na put pravca. Ulazi nepotvrđeno i potvrđuje se na mapi. */
  /** Traži koordinatu mestu koje je nema. Predlog, ne potvrda. */
  suggestPlace: (placeId: number) =>
    post<{
      id: number;
      placeName: string;
      candidates: { latitude: number; longitude: number; displayName: string }[];
      note: string | null;
    }>(`/api/corridors/places/${placeId}/suggest`, {}),

  addCorridorPlace: (id: number, placeName: string, afterSequence: number | null) =>
    post<{ id: number }>(`/api/corridors/${id}/places`, { placeName, afterSequence }),

  removeCorridorPlace: (placeId: number) =>
    del<{ obrisano: boolean }>(`/api/corridors/places/${placeId}`),

  /** Potvrđuje jedno mesto, sa koordinatom kakvu je operater ostavio na mapi. */
  confirmCorridorPlace: (id: number, latitude: number, longitude: number, verifiedBy: string) =>
    post<{ id: number }>(`/api/corridors/places/${id}/confirm`, { latitude, longitude, verifiedBy }),

  /** Potvrđuje celu liniju odjednom — ovde se potvrđuje put, ne pojedinačna tačka. */
  confirmCorridor: (id: number, verifiedBy: string) =>
    post<{ confirmed: number; withoutPoint: string[] }>(
      `/api/corridors/${id}/confirm`, { verifiedBy })
};
